import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Branded date picker. value/onChange use YYYY-MM-DD strings ("" = empty).
export function DatePicker({ value, onChange, placeholder = "Pick a date", testid, className, clearable = false, disablePast = false }) {
  const [open, setOpen] = useState(false);
  let selected;
  try { selected = value ? parseISO(value) : undefined; } catch { selected = undefined; }
  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className={cn(
              "inline-flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-600 transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100",
              !value && "text-slate-400",
              className,
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="truncate">{value && selected ? format(selected, "d MMM yyyy") : placeholder}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            disabled={disablePast ? { before: new Date(new Date().setHours(0, 0, 0, 0)) } : undefined}
            onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {clearable && value && (
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid={testid ? `${testid}-clear` : undefined}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500"
          aria-label="Clear date"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
